import { zodResolver } from '@hookform/resolvers/zod';
import { paths } from 'config/paths';
import { useAuthContext } from 'contexts/auth.context';
import { useSendOtp, useVerifyOtp } from 'lib/hooks/auth/auth.mutations';
import OTPInput, { ResendOTP } from 'otp-input-react';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import 'react-phone-input-2/lib/style.css';
import { useHistory } from 'react-router-dom';
import { countryCodeSchema, phoneNumberSchema } from 'schemas/common.schemas';
import { z } from 'zod';
import {
  isAdmin,
  isSPRPartner,
  isSubUser,
  isSysOpsUser,
} from '../../../constants/common';
import '../styles.scss';

const verifyOtpFormSchema = z.object({
  userId: z.number(),
  otp: z.string().min(6, 'Please enter a valid 6-digit OTP'),
  countryCode: countryCodeSchema,
  phone: phoneNumberSchema,
});

type VerifyOtpFormSchema = z.infer<typeof verifyOtpFormSchema>;

type VerifyOtpProps = {
  mobileLastFour: string;
};

function VerifyOtp({ mobileLastFour }: VerifyOtpProps) {
  const history = useHistory();

  const verifyOtpMutation = useVerifyOtp();

  const { user } = useAuthContext();

  const sendOtpMutation = useSendOtp();

  const {
    formState: { errors, isSubmitting },
    ...form
  } = useForm<VerifyOtpFormSchema>({
    defaultValues: {
      userId: user?.id ?? 0,
      otp: '',
      phone: user?.phone ?? '',
      countryCode: user?.countryCode ?? '1',
    },
    resolver: zodResolver(verifyOtpFormSchema),
  });
  const handleResendOtp = async () => {
    if (user?.id && user?.phone && user?.countryCode) {
      await sendOtpMutation.mutateAsync({
        userId: user.id,
      });
    }
  };
  const handleVerifyOtp = async (values: VerifyOtpFormSchema) => {
    try {
      if (!values.otp || !values.phone || !values.userId) {
        form.setError('otp', {
          message: 'OTP is required.',
        });
        return;
      }

      const value = await verifyOtpMutation.mutateAsync(values);

      if (value?.success) {
        const token = localStorage.getItem('t_Authorization');
        if (token) {
          localStorage.setItem('Authorization', token);
          localStorage.removeItem('t_Authorization');
        }

        if (user && (isAdmin(user) || isSysOpsUser(user))) {
          history.push(paths.sprDashboard.dashboard.path);
        } else if (
          user &&
          (isSPRPartner(user) || isSubUser(user)) &&
          user.lobId === 3
        ) {
          history.push(paths.fixedDashboard.path);
        } else {
          history.push(paths.dashboard.path);
        }
      }
    } catch (error: any) {
      const message = error?.data?.message || 'Something went wrong';
      form.setError('otp', {
        message,
      });
    }
  };

  return (
    <div id="verifyOTPscreen" className="verifyOTPscreen">
      <h2 className="title">Confirm your session</h2>
      <p className="subtitle">
        We have sent you a code via SMS to your number ending in{' '}
        <b className="ml-1">{mobileLastFour}</b>.
      </p>
      <p className="subtitle">
        Didn't receive a code?
        <ResendOTP
          onResendClick={handleResendOtp}
          className="otp-resend"
          renderButton={renderButton}
          renderTime={renderTime}
        />
      </p>
      <form onSubmit={form.handleSubmit(handleVerifyOtp)} noValidate>
        <div className="form-group">
          <Controller
            control={form.control}
            name="otp"
            render={({ field }) => (
              <OTPInput
                className="otp-input"
                value={field.value}
                onChange={(otp) => {
                  field.onChange(otp);
                  if (otp.length === 6) {
                    form.handleSubmit(handleVerifyOtp)();
                  }
                }}
                OTPLength={6}
                otpType="number"
                autoFocus
                secure
                data-testid="otp-input"
              />
            )}
          />
          {errors.otp && (
            <div className="invalid-feedback">{errors.otp.message}</div>
          )}
        </div>
        <button
          disabled={isSubmitting}
          type="submit"
          className="btn btn-dark btn-lg w-100"
        >
          {isSubmitting ? 'Please wait ...' : 'Login'}
        </button>
      </form>
    </div>
  );
}

const renderButton = ({
  remainingTime,
  onClick,
}: {
  remainingTime: number;
  disabled: boolean;
  onClick: () => void;
}) => (
  <button data-testid="resend-button" onClick={onClick}>
    {remainingTime !== 0 ? `Please wait ${remainingTime} sec` : 'Resend'}
  </button>
);

const renderTime = () => null;

export default VerifyOtp;
